import { useEffect, useState, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import { startConversation, sendMessage } from "../../api/webchat";
import { LanguageContext } from "../../context/LanguageContext";

export default function AgentTestDialog({ open, onClose, agent, tenantId }) {
  const { translate } = useContext(LanguageContext);
  const [messages, setMessages] = useState([]); // {role:'user'|'assistant', content:string}[]
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [error, setError] = useState("");
  const [resetting, setResetting] = useState(false);

  // Start or resume the test conversation on open
  useEffect(() => {
    if (!open) return;
    if (!agent?.id || !tenantId) {
      setError("Missing agent or tenant context");
      return;
    }
    let cancelled = false;
    (async () => {
      setError("");
      setMessages([]);
      const key = `tm:test:${tenantId}:${agent.id}`;
      const cached = localStorage.getItem(key);
      if (cached) {
        if (!cancelled) setConversationId(cached);
        return;
      }
      try {
        const resp = await startConversation(tenantId, agent.id, {
          source: "AgentTestDialog",
          resume: false,
        });
        const cid = resp?.conversationId;
        if (cid) {
          try {
            localStorage.setItem(key, cid);
          } catch {}
          if (!cancelled) setConversationId(cid);
        } else if (!cancelled) {
          setError("Failed to start conversation");
        }
      } catch (e) {
        if (!cancelled) {
          const msg =
            e?.code === "agent_disabled"
              ? "Public chat disabled for this agent"
              : e?.message || "Failed to start conversation";
          setError(msg);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, tenantId, agent?.id]);

  const doSend = async () => {
    if (!input.trim() || !agent || !tenantId || !conversationId) return;
    const userText = input.trim();
    setInput("");
    setError("");
    setMessages((m) => [...m, { role: "user", content: userText }]);
    setLoading(true);
    try {
      const resp = await sendMessage({
        tenantId,
        agentId: agent.id,
        conversationId,
        body: userText,
        imageUrls: [],
      });
      const reply =
        resp?.reply ||
        (resp?.status === "queued_no_bot"
          ? "Bot is inactive for this conversation."
          : "…");
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Backend error" },
      ]);
      setError(e?.message || "Failed to send message");
    } finally {
      setLoading(false);
    }
  };

  const doReset = async () => {
    try {
      if (!tenantId || !agent?.id) return;
      setResetting(true);
      const key = `tm:test:${tenantId}:${agent.id}`;
      try {
        localStorage.removeItem(key);
      } catch {}
      setMessages([]);
      setConversationId(null);
      setError("");
      const resp = await startConversation(tenantId, agent.id, {
        source: "AgentTestDialogReset",
        resume: false,
      });
      const cid = resp?.conversationId;
      if (cid) {
        try {
          localStorage.setItem(key, cid);
        } catch {}
        setConversationId(cid);
      } else {
        setError("Failed to start conversation");
      }
    } catch (e) {
      setError(e?.message || "Failed to reset test session");
    } finally {
      setResetting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 700 }}>
        {(translate && translate("TestChat")) || "Test chat"}: {agent?.name}
      </DialogTitle>
      <DialogContent sx={{ height: 420, overflowY: "auto", bgcolor: "background.default" }} dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {!conversationId && !error && (
          <Typography variant="body2" color="text.secondary">
            Starting conversation…
          </Typography>
        )}
        {messages.map((m, i) => (
          <Box key={i} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color={m.role === "user" ? "primary" : "secondary"}>
              {m.role === "user" ? "You" : agent?.name || "Agent"}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {m.content}
            </Typography>
          </Box>
        ))}
        {loading && (
          <Box sx={{ textAlign: "center", mt: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ gap: 1.2, px: 2, py: 1.5 }}>
        <TextField
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={(translate && translate("SendAMessage")) || "Send a message"}
          fullWidth
          onKeyDown={(e) => e.key === "Enter" && doSend()}
          disabled={!conversationId || loading}
        />
        <Button
          onClick={doSend}
          disabled={!conversationId || loading}
          variant="contained"
          sx={{
            backgroundColor: "#2B2B2B",
            color: "#FFFFFF",
            fontWeight: 600,
            textTransform: "none",
            borderRadius: "8px",
            px: 2,
            boxShadow: "none",
            "&:hover": { backgroundColor: "#3A3A3A" },
          }}
        >
          {(translate && translate("Send")) || "Send"}
        </Button>
        <Button
          onClick={doReset}
          disabled={!conversationId || resetting || loading}
          variant="outlined"
          sx={{
            borderColor: "#616161",
            color: "#E0E0E0",
            fontWeight: 600,
            textTransform: "none",
            borderRadius: "8px",
            px: 2,
            "&:hover": { borderColor: "#9E9E9E", backgroundColor: "rgba(255,255,255,0.04)" },
          }}
        >
          {(translate && translate("ResetTest")) || "Reset test"}
        </Button>
        <Button onClick={onClose} variant="text" sx={{ color: "text.secondary", textTransform: "none" }}>
          {(translate && translate("Close")) || "Close"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

